import { useEffect, useRef } from "react";
import type { KeyboardEvent, ReactNode } from "react";
import QuantityInput from "./QuantityInput";
import { ProductRecommendations } from "../recommendations";
import {
  resolveMinQuantitySimple,
  resolveStep,
} from "../../utils/productQuantity";
import type { CartMap, Product, RecommendationItem } from "../../types";

interface QuantityChangeOptions {
  source?: "catalog" | "recommendation" | string;
}

interface ProductListProps {
  promotedProducts?: Product[];
  regularProducts?: Product[];
  cart: CartMap;
  recommendationsByProduct?: Record<number, RecommendationItem[]>;
  recommendationsLoadingByProduct?: Record<number, boolean>;
  onQuantityChange: (
    product: Product,
    value: number,
    options?: QuantityChangeOptions,
  ) => void;
  headerActions?: ReactNode;
  filters?: ReactNode;
  pagination?: ReactNode;
  baseMinQuantity?: number;
  lastModifiedProductId?: number | null;
}

const ProductList = ({
  promotedProducts = [],
  regularProducts = [],
  cart,
  recommendationsByProduct = {},
  recommendationsLoadingByProduct = {},
  onQuantityChange,
  headerActions,
  filters,
  pagination,
  baseMinQuantity = 0,
  lastModifiedProductId = null,
}: ProductListProps) => {
  const inputRefs = useRef<Array<HTMLInputElement | null>>([]);
  const combined = [...promotedProducts, ...regularProducts];

  useEffect(() => {
    inputRefs.current = inputRefs.current.slice(0, combined.length);
  }, [combined.length]);

  const focusProduct = (index: number) => {
    const input = inputRefs.current[index];
    if (input) {
      input.focus();
    }
  };

  const handleEnter = (index: number, event: KeyboardEvent<HTMLInputElement>) => {
    const direction = event.shiftKey ? -1 : 1;
    const nextIndex = index + direction;
    if (nextIndex >= 0 && nextIndex < combined.length) {
      focusProduct(nextIndex);
    }
  };

  return (
    <section className="catalog list-view">
      <div className="catalog-header">
        <h2>Lista produktów</h2>
        {headerActions}
      </div>
      {filters}
      {combined.length === 0 ? (
        <p className="catalog-empty">Brak produktów do wyświetlenia.</p>
      ) : (
        <>
          {promotedProducts.length > 0 && (
            <>
              <h3 className="catalog-subheading">Promowane</h3>
              <div className="catalog-list">
                {promotedProducts.map((product, index) => {
                  const selected = cart[product.id]?.quantity || 0;
                  const recommendations = recommendationsByProduct[product.id] || [];
                  const recommendationsLoading = Boolean(
                    recommendationsLoadingByProduct[product.id],
                  );
                  return (
                    <article key={product.id} className="product-row">
                      <div className="product-row-main">
                        <h3>{product.displayName || product.name}</h3>
                        <span className="price">
                          {product.price.toFixed(2)} zł / {product.unit}
                        </span>
                        <p className="availability">{product.salesDaysLabel}</p>
                      </div>
                      <QuantityInput
                        value={selected}
                        onChange={(value) => onQuantityChange(product, value)}
                        minQuantity={resolveMinQuantitySimple(product, baseMinQuantity)}
                        step={resolveStep(product)}
                        inputRef={(element) => {
                          inputRefs.current[index] = element;
                        }}
                        onEnter={(event) => handleEnter(index, event)}
                      />
                      {selected > 0 && product.id === lastModifiedProductId && (
                        <div className="product-row-recommendations">
                          <ProductRecommendations
                            items={recommendations}
                            isLoading={recommendationsLoading}
                            cart={cart}
                            onQuantityChange={onQuantityChange}
                            baseMinQuantity={baseMinQuantity}
                          />
                        </div>
                      )}
                    </article>
                  );
                })}
              </div>
            </>
          )}
          {regularProducts.length > 0 && (
            <>
              {promotedProducts.length > 0 && (
                <h3 className="catalog-subheading">Pozostałe</h3>
              )}
              {pagination}
              <div className="catalog-list">
                {regularProducts.map((product, regularIndex) => {
                  const index = promotedProducts.length + regularIndex;
                  const selected = cart[product.id]?.quantity || 0;
                  const recommendations = recommendationsByProduct[product.id] || [];
                  const recommendationsLoading = Boolean(
                    recommendationsLoadingByProduct[product.id],
                  );
                  return (
                    <article key={product.id} className="product-row">
                      <div className="product-row-main">
                        <h3>{product.displayName || product.name}</h3>
                        <span className="price">
                          {product.price.toFixed(2)} zł / {product.unit}
                        </span>
                        <p className="availability">{product.salesDaysLabel}</p>
                      </div>
                      <QuantityInput
                        value={selected}
                        onChange={(value) => onQuantityChange(product, value)}
                        minQuantity={resolveMinQuantitySimple(product, baseMinQuantity)}
                        step={resolveStep(product)}
                        inputRef={(element) => {
                          inputRefs.current[index] = element;
                        }}
                        onEnter={(event) => handleEnter(index, event)}
                      />
                      {selected > 0 && product.id === lastModifiedProductId && (
                        <div className="product-row-recommendations">
                          <ProductRecommendations
                            items={recommendations}
                            isLoading={recommendationsLoading}
                            cart={cart}
                            onQuantityChange={onQuantityChange}
                            baseMinQuantity={baseMinQuantity}
                          />
                        </div>
                      )}
                    </article>
                  );
                })}
                {pagination}
              </div>
            </>
          )}
        </>
      )}
    </section>
  );
};

export default ProductList;
